<template>
  <figure class="highcharts-figure">
    <highcharts ref="chart" :options="options" />
    <p class="highcharts-description highcharts-linked-description">
      <strong>Figure : </strong>
      {{ $t('compGraphicsTransportsEmissionFactors.help') }}
    </p>
  </figure>
</template>

<script>
import '@/plugins/highcharts.js'
import Highcharts from 'highcharts'
import Commute from '@/models/carbon/Commute.js'
import VEHICLES_FACTORS from '@/../data/factors/carbon/vehiclesFactors.json'
import TRANSPORTS_FACTORS from '@/../data/factors/carbon/transportsFactors.json'
import { EmissionFactor } from '@/models/carbon/Factor.js'

export default {
  name: 'TransportsEmissionFactors',
  props: {
    ghgi: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      options: {}
    }
  },
  methods: {
    getAverageCarFromData () {
      let efs = {
        combustion: 0,
        upstream: 0,
        manufacturing: 0
      }
      let totalkm = 0
      for (let item of this.ghgi.commutes) {
        for (let mode of Commute.TRAVELS_MODES) {
          if (item[mode] > 0) {
            let ef = item.getEmissionFactor(
              mode,
              item.engine,
              null,
              this.ghgi.year
            )
            efs.combustion += ef.combustion.total * item[mode]
            efs.upstream += ef.upstream.total * item[mode]
            efs.manufacturing += ef.manufacturing.total * item[mode]
            totalkm += item[mode]
          }
          if (item[mode + '2'] > 0) {
            let ef2 = item.getEmissionFactor(
              mode,
              item.engine2,
              null,
              this.ghgi.year
            )
            efs.combustion += ef2.combustion.total * item[mode + '2']
            efs.upstream += ef2.upstream.total * item[mode + '2']
            efs.manufacturing += ef2.manufacturing.total * item[mode + '2']
            totalkm += item[mode + '2']
          }
        }
      }
      efs.combustion = Math.round((efs.combustion / totalkm) * 1000) / 1000
      efs.upstream = Math.round((efs.upstream / totalkm) * 1000) / 1000
      efs.manufacturing =
        Math.round((efs.manufacturing / totalkm) * 1000) / 1000
      return efs
    },
    compute () {
      let self = this
      let avgFromData = this.getAverageCarFromData()
      let colors = Highcharts.getOptions().colors
      let categories = []
      let series = [
        {
          id: 'id1',
          name: this.$t('combustion'),
          data: [avgFromData.combustion],
          groupPadding: 0.5,
          pointWidth: 50,
          color: colors[0]
        },
        {
          id: 'id2',
          name: this.$t('upstream'),
          data: [avgFromData.upstream],
          groupPadding: 0.5,
          pointWidth: 50,
          color: colors[1]
        },
        {
          id: 'id3',
          name: this.$t('manufacturing'),
          data: [avgFromData.manufacturing],
          groupPadding: 0.5,
          pointWidth: 50,
          color: colors[2]
        },
        {
          linkedTo: 'id1',
          name: this.$t('combustion'),
          xAxis: 1,
          data: [],
          groupPadding: 0.5,
          pointWidth: 50,
          color: colors[0]
        },
        {
          linkedTo: 'id2',
          name: this.$t('upstream'),
          xAxis: 1,
          data: [],
          groupPadding: 0.5,
          pointWidth: 50,
          color: colors[1]
        },
        {
          linkedTo: 'id3',
          name: this.$t('manufacturing'),
          xAxis: 1,
          data: [],
          groupPadding: 0.5,
          pointWidth: 50,
          color: colors[2]
        }
      ]
      // add electric bike
      categories.push(this.$t('ebike'))
      let ef = EmissionFactor.createFromObj(VEHICLES_FACTORS.bike.electric)
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add motorbike
      categories.push(this.$t('motorbike'))
      ef = EmissionFactor.createFromObj(VEHICLES_FACTORS.motorbike.gasoline)
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add car
      categories.push(this.$t('car'))
      ef = EmissionFactor.createFromObj(VEHICLES_FACTORS.car.gasoline)
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add bus
      categories.push(this.$t('bus'))
      ef = EmissionFactor.createFromObj(TRANSPORTS_FACTORS.bus['bus.bigcity'])
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add busintercity
      categories.push(this.$t('busintercity'))
      ef = EmissionFactor.createFromObj(TRANSPORTS_FACTORS.bus['bus.intercity'])
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add tram
      categories.push(this.$t('tram'))
      ef = EmissionFactor.createFromObj(
        TRANSPORTS_FACTORS.railway['tram.bigcity']
      )
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add train
      categories.push(this.$t('train'))
      ef = EmissionFactor.createFromObj(
        TRANSPORTS_FACTORS.railway['train.longdistance']
      )
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add expressrailway
      categories.push(this.$t('expressrailway'))
      ef = EmissionFactor.createFromObj(TRANSPORTS_FACTORS.railway['rer'])
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add subway
      categories.push(this.$t('subway'))
      ef = EmissionFactor.createFromObj(TRANSPORTS_FACTORS.railway['subway'])
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      // add cable car
      categories.push(this.$t('cablecar'))
      ef = EmissionFactor.createFromObj(
        TRANSPORTS_FACTORS['guided.transport']['cable.car']
      )
      series[3].data.push(ef.getFactor().combustion.total)
      series[4].data.push(ef.getFactor().upstream.total)
      series[5].data.push(ef.getFactor().manufacturing.total)
      this.options = {
        chart: {
          type: 'column',
          events: {
            render: function () {
              let ren = this.renderer
              ren
                .path(['M', 178, 10, 'L', 178, 240])
                .attr({
                  'stroke-width': 2,
                  stroke: 'gray',
                  dashstyle: 'dash'
                })
                .add()
            }
          }
        },
        title: {
          text: ''
        },
        xAxis: [
          {
            width: '20%',
            type: 'category',
            categories: [this.$t('compGraphicsTransportsEmissionFactors.data')],
            labels: {
              rotation: -45
            }
          },
          {
            offset: 0,
            width: '80%',
            left: '20%',
            type: 'category',
            categories: categories
          }
        ],
        yAxis: {
          min: 0,
          title: {
            text: this.$t('compGraphicsTransportsEmissionFactors.kg.co2e.km')
          }
        },
        tooltip: {
          formatter: function () {
            let tooltip = this.point.category + ' <br />'
            tooltip +=
              '<span style="color:' +
              this.point.series.color +
              ';">\u25CF ' +
              '</span> '
            tooltip +=
              this.point.series.name +
              ': <b>' +
              Math.round(this.point.y * 10000) / 10000
            tooltip +=
              '</b> ' +
              self.$t('compGraphicsTransportsEmissionFactors.kg.co2e.km') +
              ' <br />'
            return tooltip
          }
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            dataLabels: {
              enabled: true
            }
          }
        },
        series: series
      }
    }
  },
  mounted () {
    this.compute()
  }
}
</script>

<style scoped>
.highcharts-figure {
  min-width: 320px;
  max-width: 700px;
  margin: 1em auto;
}
.highcharts-description {
  text-align: center;
  color: '#777777';
  font-style: italic;
}
</style>
